import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { Instagram, LogOut, LinkIcon } from 'lucide-react'
import { api } from '@/lib/api'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Separator } from '@/components/ui/separator'

interface UserConfig {
  email: string
  instagram_username: string | null
  profile_picture_url: string
  dm_message: string
  comment_reply: string
  is_active: boolean
}

export default function Dashboard() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { logout } = useAuth()

  const [config, setConfig] = useState<UserConfig | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const [dmMessage, setDmMessage] = useState('')
  const [commentReply, setCommentReply] = useState('')
  const [isActive, setIsActive] = useState(false)

  const autoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isFirstLoad = useRef(true)

  const loadConfig = useCallback(async () => {
    try {
      const data = await api.me()
      const mapped: UserConfig = {
        email: data.user?.email || '',
        instagram_username: data.ig?.username || null,
        profile_picture_url: data.ig?.profile_picture_url || '',
        dm_message: data.ig?.dm_message || '',
        comment_reply: data.ig?.comment_reply || '',
        is_active: data.ig?.is_active || false,
      }
      setConfig(mapped)
      setDmMessage(mapped.dm_message)
      setCommentReply(mapped.comment_reply)
      setIsActive(mapped.is_active)
      isFirstLoad.current = false
    } catch (err: any) {
      if (err.message?.includes('401') || err.message?.includes('Unauthorized') || err.message?.includes('token')) {
        api.clearToken()
        navigate('/')
      } else {
        toast.error(err.message)
      }
    } finally {
      setLoading(false)
    }
  }, [navigate])

  useEffect(() => {
    loadConfig()
  }, [loadConfig])

  // Auto-save with debounce when fields change
  useEffect(() => {
    if (isFirstLoad.current) return
    if (!config?.instagram_username) return
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current)
    autoSaveTimer.current = setTimeout(async () => {
      try {
        await api.saveConfig({ dm_message: dmMessage, comment_reply: commentReply, is_active: isActive })
        setSaving(false)
      } catch {}
    }, 1500)
    setSaving(true)
    return () => { if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current) }
  }, [dmMessage, commentReply, isActive, config?.instagram_username])

  // Handle redirect-back from OAuth
  useEffect(() => {
    if (searchParams.get('connected') === '1') {
      setSearchParams({}, { replace: true })
      loadConfig()
      toast.success('Instagram conectado!')
    }
  }, [searchParams, setSearchParams, loadConfig])

  function handleConnectInstagram() {
    window.location.href = api.getOAuthUrl()
  }

  async function handleSave() {
    setSaving(true)
    try {
      await api.saveConfig({ dm_message: dmMessage, comment_reply: commentReply, is_active: isActive })
      toast.success('Salvo!')
    } catch (err: any) {
      toast.error(err.message)
    } finally {
      setSaving(false)
    }
  }

  async function handleDisconnect() {
    try {
      await api.disconnect()
      await loadConfig()
      toast.success('Instagram desconectado.')
    } catch (err: any) {
      toast.error(err.message)
    }
  }

  function handleLogout() {
    api.clearToken()
    logout()
    navigate('/')
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Header */}
      <header className="border-b border-zinc-800 bg-black/60 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="Missão 14" className="h-8 w-auto" />
          </div>
          <div className="flex items-center gap-3">
            {config && (
              <span className="text-zinc-500 text-sm hidden sm:block">{config.email}</span>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-zinc-400 hover:text-white gap-1.5"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Sair</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-2xl mx-auto px-4 py-8">
        {loading ? (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-6 space-y-4">
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-4 w-72" />
              <Skeleton className="h-10 w-40" />
              <Separator className="bg-zinc-800" />
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-10 w-full" />
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-6 space-y-6">

              {/* Instagram connection status */}
              {config?.instagram_username ? (
                <>
                  {/* State B: Connected */}
                  <div className="flex items-center gap-3">
                    {config.profile_picture_url ? (
                      <img
                        src={config.profile_picture_url}
                        alt={config.instagram_username || ''}
                        className="w-12 h-12 rounded-full object-cover border-2 border-zinc-700"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-zinc-700 flex items-center justify-center">
                        <Instagram className="w-6 h-6 text-zinc-400" />
                      </div>
                    )}
                    <div>
                      <p className="font-semibold text-white">@{config.instagram_username}</p>
                      <p className="text-xs text-green-400 flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block" />
                        Conectado
                      </p>
                    </div>
                  </div>

                  <Separator className="bg-zinc-800" />

                  {/* Toggle */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-white">Auto DM</p>
                      <p className="text-sm text-zinc-500">Ativar envio automático de DMs</p>
                    </div>
                    <Switch
                      checked={isActive}
                      onCheckedChange={setIsActive}
                    />
                  </div>

                  <Separator className="bg-zinc-800" />

                  {/* DM message */}
                  <div className="space-y-2">
                    <Label className="text-zinc-300 font-medium">Mensagem DM</Label>
                    <Textarea
                      placeholder="Cole o link ou mensagem enviada automaticamente por DM..."
                      value={dmMessage}
                      onChange={e => setDmMessage(e.target.value)}
                      className="min-h-[100px] resize-none"
                    />
                    <p className="text-xs text-zinc-600">
                      Enviada automaticamente para quem comentar nos seus posts.
                    </p>
                  </div>

                  {/* Comment reply */}
                  <div className="space-y-2">
                    <Label className="text-zinc-300 font-medium">Resposta no comentário</Label>
                    <Input
                      placeholder='ex: Mandei um link na sua DM! 😉  (deixe vazio para não responder)'
                      value={commentReply}
                      onChange={e => setCommentReply(e.target.value)}
                    />
                    <p className="text-xs text-zinc-600">
                      Resposta pública deixada no comentário após o DM ser enviado.
                    </p>
                  </div>

                  {/* Action buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 pt-2">
                    <div className="flex-1 flex items-center gap-2">
                      <span className={`text-xs transition-opacity ${saving ? 'opacity-100 text-zinc-400' : 'opacity-0'}`}>
                        Salvando...
                      </span>
                      {!saving && (
                        <span className="text-xs text-zinc-600">Salva automaticamente</span>
                      )}
                    </div>
                    <Button
                      variant="outline"
                      onClick={handleDisconnect}
                      className="border-red-900/50 text-red-400 hover:bg-red-950/30 hover:border-red-700 hover:text-red-300"
                    >
                      Desconectar Instagram
                    </Button>
                  </div>
                </>
              ) : (
                /* State A: Not connected */
                <div className="flex flex-col items-center text-center py-8 space-y-5">
                  <div className="w-16 h-16 rounded-full bg-pink-950/40 border border-pink-900/50 flex items-center justify-center">
                    <Instagram className="w-8 h-8 text-pink-400" />
                  </div>
                  <div className="space-y-1.5">
                    <h2 className="text-xl font-semibold text-white">Conecte seu Instagram</h2>
                    <p className="text-zinc-500 text-sm max-w-sm">
                      Quando alguém comentar nos seus posts, a SIMIA manda o link automaticamente.
                    </p>
                  </div>
                  <Button
                    onClick={handleConnectInstagram}
                    className="bg-[#1877f2] hover:bg-[#166fe5] text-white font-semibold gap-2 px-6"
                  >
                    <LinkIcon className="w-4 h-4" />
                    Conectar com Instagram
                  </Button>
                  <p className="text-xs text-zinc-700">
                    Você será redirecionado para o login do Instagram / Meta
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  )
}
